import React, { useState, useMemo } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useApp, LOGOS } from '../context/AppContext';
import { useI18n } from '../i18n';
import { USERS, OFFICES } from '../data/database';

const AuthModal = () => {
  const { showAuthModal, setShowAuthModal, authMode, setAuthMode, login, loginError } = useApp();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAllAccounts, setShowAllAccounts] = useState(false);

  // Genera account demo dal database
  const DEMO_ACCOUNTS = useMemo(() => {
    return USERS.map(user => {
      let label = 'Consumer';
      if (user.role === 'admin') {
        label = 'Admin';
      } else if (user.role === 'partner') {
        const office = OFFICES.find(o => o.id === user.officeId);
        label = office?.name?.split(' - ')[0] || office?.name?.split(' ')[0] || 'Partner';
      }
      return {
        label,
        email: user.email,
        password: user.password,
        role: user.role,
      };
    });
  }, []);

  if (!showAuthModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    await new Promise(resolve => setTimeout(resolve, 300));

    if (authMode === 'login') {
      const success = login(email, password);
      if (!success) {
        setError('Email o password non corretti');
      }
    } else {
      setAuthMode('login');
      setEmail('');
      setPassword('');
    }
    
    setIsLoading(false);
  };

  const fillDemoCredentials = (account) => {
    setEmail(account.email);
    setPassword(account.password);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-center">
          <button 
            onClick={() => setShowAuthModal(false)}
            className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <img src={LOGOS.dark} alt="Skipres" className="h-10 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white">
            {authMode === 'login' ? t('authPage', 'welcomeBack') : t('authPage', 'createAccount')}
          </h2>
          <p className="text-emerald-100 text-sm mt-1">
            {authMode === 'login' ? t('authPage', 'loginToContinue') : t('authPage', 'registerToStart')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Demo accounts selector */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
            <p className="text-sm text-emerald-700 font-semibold mb-2">Account Demo:</p>
            <div className="flex flex-wrap gap-1.5">
              {DEMO_ACCOUNTS.slice(0, showAllAccounts ? DEMO_ACCOUNTS.length : 4).map((acc) => (
                <button
                  key={acc.email}
                  type="button"
                  data-testid={`demo-account-${acc.label.toLowerCase()}`}
                  onClick={() => fillDemoCredentials(acc)}
                  className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${
                    email === acc.email 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-white text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                  }`}
                >
                  {acc.label}
                </button>
              ))}
            </div>
            {!showAllAccounts && (
              <button type="button" onClick={() => setShowAllAccounts(true)} className="text-xs text-emerald-600 font-medium mt-2 hover:underline">
                Mostra tutti gli account...
              </button>
            )}
          </div>

          {authMode === 'register' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('authPage', 'name')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('authPage', 'yourName')}
                  className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth', 'email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('authPage', 'yourEmail')}
                className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth', 'password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('authPage', 'yourPassword')}
                className="w-full pl-10 pr-12 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {authMode === 'login' && (
            <button type="button" className="text-sm text-emerald-600 font-medium mb-4 hover:underline">
              {t('auth', 'forgotPassword')}
            </button>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {authMode === 'login' ? t('common', 'login') : t('common', 'register')}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              {authMode === 'login' ? t('auth', 'noAccount') : t('auth', 'haveAccount')}
              <button
                type="button"
                onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                className="text-emerald-600 font-medium ml-1 hover:underline"
              >
                {authMode === 'login' ? t('common', 'register') : t('common', 'login')}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
